import Card from "@material-ui/core/Card";
import { makeStyles, Theme } from "@material-ui/core/styles";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { MouseEventHandler, useEffect, useState } from "react";
import { changeAppState } from "../../../../../../../../common/appState";
import { useVirtualScrollDummyCard } from "../../../../../../../../common/hooks/useVirtualScrollDummyCard";
import { relativeScroll } from "../../../../../../../../common/util/Scroll/relativeScroll";
import { PrimaryButton } from "../../../../../../../shared/Button/PrimaryButton";
import { SuccessButton } from "../../../../../../../shared/Button/SuccessButton";
import { Link } from "../../../../../../../shared/Link/LinkWithYouTube";
import { claimRewardCoin } from "./functions";
import { Quest, Reward, TransitionFunc } from "./quests/types";

export const myPageQuestCardScrollableContainerHeight = 308;

export function QuestRecord({
    quest: { title, reward, transitionTarget, questKey },
    isOngoing,
    claimed,
    questKeyToScrollTo,
    scrollableElement,
    claimingAll,
    scrollTop,
    index,
}: {
    quest: Quest;
    isOngoing?: boolean;
    claimed?: boolean;
    questKeyToScrollTo: string;
    scrollableElement: HTMLDivElement | null;
    claimingAll: boolean;
    scrollTop: number;
    index: number;
}) {
    const c = useStyles({ isOngoing, claimed });

    const { cardRef, isDummyCard } = useVirtualScrollDummyCard(
        scrollTop,
        myPageQuestCardScrollableContainerHeight,
        cardHeight,
        index
    );

    const scrollToThisRecord = () => {
        if (!cardRef.current || !scrollableElement) {
            setTimeout(scrollToThisRecord, 200);
            return;
        }
        relativeScroll(scrollableElement, cardRef.current, 5);
    };

    useEffect(() => {
        if (questKeyToScrollTo !== questKey) {
            return;
        }
        scrollToThisRecord();
    }, [questKeyToScrollTo, questKey, scrollableElement]);

    if (isDummyCard) {
        return <div className={c.dummyCard} ref={cardRef} key="outsideCard" />;
    }

    return (
        <div ref={cardRef} key="outsideCard">
            <Card className={c.card} elevation={claimed ? 0 : undefined}>
                <div className={c.checkContainer}>
                    <CheckCircleOutlineIcon className={c.checkIcon} />
                </div>
                <div className={c.labelsAndButton}>
                    <div className={c.titleAndReward}>
                        <div className={c.title}>{title}</div>
                        <div className={c.reward}>
                            Reward: {reward.value.toLocaleString()}{" "}
                            {reward.type}
                            {reward.type === "coin" && reward.value > 1 && "s"}
                        </div>
                    </div>
                    <div className={c.buttonContainer}>
                        {isOngoing ? (
                            typeof transitionTarget === "string" ? (
                                <Link to={transitionTarget}>
                                    <StartButton
                                        onClick={() => {
                                            changeAppState("signInPanelState", {
                                                type: "close",
                                            });
                                        }}
                                    />
                                </Link>
                            ) : (
                                <StartButton onClick={transitionTarget} />
                            )
                        ) : (
                            <ClaimButton
                                claimed={claimed}
                                questKey={questKey}
                                questTitle={title}
                                reward={reward}
                                transitionTarget={transitionTarget}
                                claimingAll={claimingAll}
                            />
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
}
const cardHeight = 50;
const cardMargin = 5;
const useStyles = makeStyles<Theme, { isOngoing?: boolean; claimed?: boolean }>(
    theme => ({
        card: ({ isOngoing, claimed }) => ({
            margin: cardMargin,
            display: "flex",
            padding: 5,
            height: cardHeight,
            justifyContent: "center",
            width: "calc(100% - 10px)",
            position: "relative",
            overflow: "visible",
            border: !isOngoing && !claimed ? "solid" : undefined,
            borderColor:
                !isOngoing && !claimed ? theme.palette.success.main : undefined,
            backgroundColor: claimed ? theme.palette.grey[200] : undefined,
            color: claimed ? theme.palette.grey[600] : undefined,
        }),
        dummyCard: {
            height: cardHeight,
            margin: cardMargin,
        },
        checkContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginLeft: 0,
            marginRight: 10,
            minWidth: 30,
        },
        checkIcon: ({ isOngoing }) => ({
            width: 30,
            height: 30,
            color: isOngoing
                ? theme.palette.grey[400]
                : theme.palette.success.main,
        }),
        labelsAndButton: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            width: "calc(100% - 40px)",
            height: "100%",
        },
        titleAndReward: {
            width: "100%",
            paddingRight: 5,
            lineHight: 1.2,
            maxWidth: "100%",
            overflow: "hidden",
        },
        title: ({ claimed }) => ({
            fontWeight: !claimed ? "bold" : undefined,
            whiteSpace: "nowrap",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
        }),
        reward: {
            color: theme.palette.grey[600],
            fontSize: "small",
        },
        buttonContainer: {
            width: 70,
            display: "flex",
            justifyContent: "flex-end",
            height: "100%",
            alignItems: "center",
            position: "relative",
        },
    })
);

function StartButton({
    onClick,
}: {
    onClick?: MouseEventHandler<HTMLButtonElement>;
}) {
    return (
        <PrimaryButton
            style={{
                maxHeight: 30,
                minHeight: 30,
                lineHeight: 1,
                fontSize: undefined,
                width: 70,
            }}
            onClick={onClick}
        >
            Start
        </PrimaryButton>
    );
}

function ClaimButton({
    claimed,
    questKey,
    questTitle,
    reward,
    transitionTarget,
    claimingAll,
}: {
    claimed?: boolean;
    questKey: string;
    questTitle: string;
    reward: Reward;
    transitionTarget: string | TransitionFunc;
    claimingAll: boolean;
}) {
    const [submitting, setSubmitting] = useState(false);
    return (
        <SuccessButton
            style={{
                lineHeight: 1,
                fontSize: undefined,
                width: 70,
            }}
            disabled={claimed || submitting || claimingAll}
            onClick={async () => {
                switch (reward.type) {
                    case "coin": {
                        await claimRewardCoin(setSubmitting, [
                            {
                                questKey,
                                questTitle,
                                coins: reward.value,
                                transitionTarget,
                            },
                        ]);
                        break;
                    }
                    case "xp": {
                        // Implement if necessary
                        break;
                    }
                    default: {
                        const _exhaustiveCheck: never = reward.type;
                    }
                }
            }}
        >
            Claim Reward
        </SuccessButton>
    );
}
